import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { type Product } from '@/mocks/products';

export interface CartItem {
  id: string;
  product: Product;
  quantity: number;
  variantId?: string;
  variantLabel?: string;
  variantPrice?: number;
}

export interface CustomerInfo {
  name: string;
  phone: string;
  address: string;
  city: string;
  pincode: string;
  orderNotes: string;
}

interface CartState {
  items: CartItem[];
  customerInfo: CustomerInfo;
}

interface CartContextType {
  items: CartItem[];
  customerInfo: CustomerInfo;
  addItem: (product: Product, quantity?: number, variantId?: string) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  setCustomerInfo: (info: Partial<CustomerInfo>) => void;
  itemCount: number;
  subtotal: number;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
}

const CartContext = createContext<CartContextType | null>(null);

const STORAGE_KEY = 'pouncebounce-cart';

function loadCartState(): CartState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return { items: [], customerInfo: { name: '', phone: '', address: '', city: '', pincode: '', orderNotes: '' } };
}

function saveCartState(state: CartState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<CartState>(loadCartState);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    saveCartState(state);
  }, [state]);

  const addItem = useCallback((product: Product, quantity: number = 1, variantId?: string) => {
    setState(prev => {
      const itemKey = variantId ? `${product.id}-${variantId}` : product.id;
      const existing = prev.items.find(i => {
        const ek = i.variantId ? `${i.product.id}-${i.variantId}` : i.product.id;
        return ek === itemKey;
      });

      let newItems: CartItem[];
      if (existing) {
        newItems = prev.items.map(i => {
          const ek = i.variantId ? `${i.product.id}-${i.variantId}` : i.product.id;
          return ek === itemKey ? { ...i, quantity: i.quantity + quantity } : i;
        });
      } else {
        const variant = variantId ? product.variants?.find(v => v.id === variantId) : undefined;
        newItems = [...prev.items, {
          id: itemKey,
          product,
          quantity,
          variantId,
          variantLabel: variant?.label,
          variantPrice: variant?.price,
        }];
      }
      return { ...prev, items: newItems };
    });
    setIsCartOpen(true);
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setState(prev => ({ ...prev, items: prev.items.filter(i => i.id !== itemId) }));
  }, []);

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    if (quantity <= 0) {
      setState(prev => ({ ...prev, items: prev.items.filter(i => i.id !== itemId) }));
      return;
    }
    setState(prev => ({
      ...prev,
      items: prev.items.map(i => i.id === itemId ? { ...i, quantity } : i),
    }));
  }, []);

  const clearCart = useCallback(() => {
    setState(prev => ({ ...prev, items: [] }));
  }, []);

  const setCustomerInfo = useCallback((info: Partial<CustomerInfo>) => {
    setState(prev => ({ ...prev, customerInfo: { ...prev.customerInfo, ...info } }));
  }, []);

  const itemCount = state.items.reduce((sum, i) => sum + i.quantity, 0);

  const subtotal = state.items.reduce((sum, i) => {
    const price = i.variantPrice ?? i.product.price;
    return sum + price * i.quantity;
  }, 0);

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  return (
    <CartContext.Provider value={{
      items: state.items,
      customerInfo: state.customerInfo,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      setCustomerInfo,
      itemCount,
      subtotal,
      isCartOpen,
      openCart,
      closeCart,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}